import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { useSEO } from "@/hooks/useSEO";
import { RoadmapCard } from "@/components/RoadmapCard";
import { RoadmapFilters } from "@/components/RoadmapFilters";
import { FeatureRequestDialog } from "@/components/FeatureRequestDialog";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";

const Roadmap = () => {
  useSEO({
    title: 'Product Roadmap - Upcoming Features | Ommni',
    description: 'View Ommni\'s product roadmap. See what we\'re building next, vote on features, and submit your own ideas. Transparent development roadmap updated regularly.',
    keywords: 'product roadmap, feature requests, upcoming features, product development, platform roadmap',
    canonical: 'https://ommni.io/roadmap',
  });

  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Check URL params for search query
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const search = params.get("search");
    if (search) {
      setSearchQuery(search);
    }
  }, []);

  const { data: roadmapItems, isLoading } = useQuery({
    queryKey: ["roadmap-items"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roadmap_items")
        .select("*")
        .eq("is_public", true)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Get unique categories
  const categories = useMemo(() => {
    if (!roadmapItems) return [];
    return [...new Set(roadmapItems.map((item) => item.category))];
  }, [roadmapItems]);

  // Filter items
  const filteredItems = useMemo(() => {
    if (!roadmapItems) return [];

    return roadmapItems.filter((item) => {
      const matchesStatus = selectedStatus === "all" || item.status === selectedStatus;
      const matchesCategory = selectedCategory === "all" || item.category === selectedCategory;
      const matchesSearch =
        searchQuery === "" ||
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesStatus && matchesCategory && matchesSearch;
    });
  }, [roadmapItems, selectedStatus, selectedCategory, searchQuery]);

  // Group items by status
  const groupedItems = useMemo(() => {
    const groups: Record<string, typeof filteredItems> = {
      planned: [],
      in_progress: [],
      completed: [],
    };

    filteredItems.forEach((item) => {
      if (item.status !== "cancelled") {
        groups[item.status]?.push(item);
      }
    });

    return groups;
  }, [filteredItems]);

  return (
    <div className="min-h-screen" itemScope itemType="https://schema.org/WebPage">
      <Navigation />
      <main className="pt-32 px-4 pb-24" role="main">
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Product Roadmap
            </h1>
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              See what we're working on and what's coming next. Vote for features you'd like to see prioritized.
            </p>
            <FeatureRequestDialog categories={categories} />
          </div>

          {/* Filters */}
          <div className="mb-12">
            <RoadmapFilters
              selectedStatus={selectedStatus}
              setSelectedStatus={setSelectedStatus}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              categories={categories}
            />
          </div>

          {/* Roadmap Grid */}
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-64" />
              ))}
            </div>
          ) : selectedStatus === "all" ? (
            // Kanban view when showing all statuses
            <div className="grid md:grid-cols-3 gap-6">
              {/* Planned Column */}
              <div className="bg-card/50 rounded-lg border-2 border-border p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                  <h2 className="text-xl font-bold">Planned</h2>
                  <span className="px-3 py-1 rounded-full bg-blue-500/10 text-blue-500 text-sm font-medium">
                    {groupedItems.planned.length}
                  </span>
                </div>
                <div className="space-y-4 min-h-[400px]">
                  {groupedItems.planned.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No planned items
                    </div>
                  ) : (
                    groupedItems.planned.map((item) => (
                      <RoadmapCard
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        description={item.description}
                        status={item.status as any}
                        category={item.category}
                        quarter={item.quarter}
                        priority={item.priority as any}
                        votes={item.votes}
                        expectedDate={item.expected_date || undefined}
                        completedDate={item.completed_date || undefined}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* In Progress Column */}
              <div className="bg-card/50 rounded-lg border-2 border-border p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                  <h2 className="text-xl font-bold">In Progress</h2>
                  <span className="px-3 py-1 rounded-full bg-orange-500/10 text-orange-500 text-sm font-medium">
                    {groupedItems.in_progress.length}
                  </span>
                </div>
                <div className="space-y-4 min-h-[400px]">
                  {groupedItems.in_progress.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No items in progress
                    </div>
                  ) : (
                    groupedItems.in_progress.map((item) => (
                      <RoadmapCard
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        description={item.description}
                        status={item.status as any}
                        category={item.category}
                        quarter={item.quarter}
                        priority={item.priority as any}
                        votes={item.votes}
                        expectedDate={item.expected_date || undefined}
                        completedDate={item.completed_date || undefined}
                      />
                    ))
                  )}
                </div>
              </div>

              {/* Completed Column */}
              <div className="bg-card/50 rounded-lg border-2 border-border p-6">
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                  <h2 className="text-xl font-bold">Completed</h2>
                  <span className="px-3 py-1 rounded-full bg-green-500/10 text-green-500 text-sm font-medium">
                    {groupedItems.completed.length}
                  </span>
                </div>
                <div className="space-y-4 min-h-[400px]">
                  {groupedItems.completed.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No completed items
                    </div>
                  ) : (
                    groupedItems.completed.map((item) => (
                      <RoadmapCard
                        key={item.id}
                        id={item.id}
                        title={item.title}
                        description={item.description}
                        status={item.status as any}
                        category={item.category}
                        quarter={item.quarter}
                        priority={item.priority as any}
                        votes={item.votes}
                        expectedDate={item.expected_date || undefined}
                        completedDate={item.completed_date || undefined}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>
          ) : (
            // Grid view when filtering by specific status
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredItems.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <p className="text-muted-foreground text-lg">No items found matching your filters.</p>
                </div>
              ) : (
                filteredItems.map((item) => (
                  <RoadmapCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    description={item.description}
                    status={item.status as any}
                    category={item.category}
                    quarter={item.quarter}
                    priority={item.priority as any}
                    votes={item.votes}
                    expectedDate={item.expected_date || undefined}
                    completedDate={item.completed_date || undefined}
                  />
                ))
              )}
            </div>
          )}
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Roadmap;
